import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { AlertCircle, Loader2, Users, X } from 'lucide-react';
import type {
  CncSpeciesAssignment,
  ProposalNodeSet,
  RfqProject,
  SupplierOrg,
  TimberSupplierInvite,
  TimberSupplierStructureRef,
} from '../../types';
import { db } from '../../utils/firebase';
import { proposalBoardFeetBySpecies } from '../../utils/cncRfqAssignments';
import { rfqProjectSupplierOrgIds } from '../../utils/rfqProjectSuppliers';
import { filterNodeSetsForInvite } from '../../utils/timberRfqStructureScope';
import { buildTimberSupplierInvites } from '../../utils/timberRfqSuppliers';
import { normalizeSupplierOrg, orgHasCapability } from '../../utils/vendorHelpers';
import SupplierShipToFields, {
  defaultShipToConfigForOrg,
  initialShipToConfigForOrg,
  type ProjectSiteAddressInput,
} from './SupplierShipToFields';
import type { SupplierShipToConfig } from '../../utils/supplierShipTo';
import './TimberRfqSuppliersModal.css';

export interface CncRfqSuppliersModalProps {
  project: RfqProject;
  projectSupplierOrgIds: string[];
  cncCapableOrgs: SupplierOrg[];
  projectSite: ProjectSiteAddressInput;
  /** Timber RFQ on the same deal — job site applies when supplier is on both work types. */
  timberRfqProject?: RfqProject | null;
  /** All supplier orgs — used to name source timber suppliers on species assignments. */
  supplierOrgById?: Map<string, SupplierOrg>;
  onClose: () => void;
  onSave: (updates: {
    supplierOrgIds: string[];
    supplierOrgId: string;
    supplierOrgName?: string;
    timberSupplierInvites: TimberSupplierInvite[];
    approvedSupplierOrgId?: string;
  }) => Promise<void>;
}

function initialShipToByOrg(
  project: RfqProject,
  orgIds: string[],
  orgs: SupplierOrg[],
  timberRfqProject: RfqProject | null | undefined,
): Record<string, SupplierShipToConfig> {
  const out: Record<string, SupplierShipToConfig> = {};
  for (const orgId of orgIds) {
    const org = orgs.find(o => o.id === orgId);
    if (!org) continue;
    const invite = project.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
    out[orgId] = initialShipToConfigForOrg(org, invite, {
      workType: 'cnc_quote',
      timberProject: timberRfqProject,
      cncProject: project,
    });
  }
  return out;
}

function initialAssignmentsByOrg(project: RfqProject): Record<string, CncSpeciesAssignment[]> {
  const out: Record<string, CncSpeciesAssignment[]> = {};
  for (const invite of project.timberSupplierInvites ?? []) {
    if (invite.cncSpeciesAssignments?.length) {
      out[invite.supplierOrgId] = invite.cncSpeciesAssignments.map(a => ({ ...a }));
    }
  }
  return out;
}

const CncRfqSuppliersModal: React.FC<CncRfqSuppliersModalProps> = ({
  project,
  projectSupplierOrgIds,
  cncCapableOrgs,
  projectSite,
  timberRfqProject,
  supplierOrgById,
  onClose,
  onSave,
}) => {
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(() => [...projectSupplierOrgIds]);
  const [shipToByOrg, setShipToByOrg] = useState<Record<string, SupplierShipToConfig>>(() =>
    initialShipToByOrg(project, projectSupplierOrgIds, cncCapableOrgs, timberRfqProject),
  );
  const [assignmentsByOrg, setAssignmentsByOrg] = useState<Record<string, CncSpeciesAssignment[]>>(() =>
    initialAssignmentsByOrg(project),
  );
  const [structureScopeByOrg, setStructureScopeByOrg] = useState<Record<string, 'all' | 'selected'>>(() => {
    const out: Record<string, 'all' | 'selected'> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.structureScope === 'selected' && invite.structures?.length) {
        out[invite.supplierOrgId] = 'selected';
      }
    }
    return out;
  });
  const [structuresByOrg, setStructuresByOrg] = useState<Record<string, TimberSupplierStructureRef[]>>(() => {
    const out: Record<string, TimberSupplierStructureRef[]> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.structures?.length) out[invite.supplierOrgId] = invite.structures;
    }
    return out;
  });
  const [proposalNodeSets, setProposalNodeSets] = useState<ProposalNodeSet[]>([]);
  const [proposalLoaded, setProposalLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', project.dealId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as { nodeSets?: ProposalNodeSet[] };
        setProposalNodeSets((data.nodeSets ?? []).filter(ns => ns.enabled !== false));
      } catch (err) {
        console.error('Could not load proposal for CNC species assignments:', err);
      } finally {
        if (!cancelled) setProposalLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.dealId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const selectedSet = useMemo(() => new Set(selectedOrgIds), [selectedOrgIds]);

  const structureRefMatchesNodeSet = (ref: TimberSupplierStructureRef, ns: ProposalNodeSet): boolean =>
    ref.id === ns.id
    || (!!ref.name.trim() && ref.name.trim().toLowerCase() === ns.name.trim().toLowerCase());

  // Node sets in a supplier's structure scope. Falls back to all node sets when
  // nothing matches so the bdft prefill stays useful while refs are being edited
  // (publish itself blocks on unmatched scopes instead of falling back).
  const scopedNodeSets = (
    scope: 'all' | 'selected',
    structures: TimberSupplierStructureRef[],
  ): ProposalNodeSet[] => {
    if (scope !== 'selected' || !structures.length) return proposalNodeSets;
    const scoped = filterNodeSetsForInvite(proposalNodeSets, { structureScope: 'selected', structures });
    return scoped.length ? scoped : proposalNodeSets;
  };

  const bdftBySpeciesForOrg = (orgId: string): Record<string, number> =>
    proposalBoardFeetBySpecies(
      scopedNodeSets(structureScopeByOrg[orgId] ?? 'all', structuresByOrg[orgId] ?? []),
    );

  const speciesOptionsForOrg = (orgId: string, orgBdft: Record<string, number>): string[] => {
    const set = new Set(Object.keys(orgBdft));
    for (const a of assignmentsByOrg[orgId] ?? []) {
      if (a.species) set.add(a.species);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  };

  // Source timber suppliers for "LCTF ships" — the deal's timber RFQ roster,
  // falling back to every timber-capable org when no timber RFQ exists.
  const sourceSupplierOptions = useMemo(() => {
    const orgs: SupplierOrg[] = [];
    const seen = new Set<string>();
    const push = (org: SupplierOrg | undefined) => {
      if (org?.id && !seen.has(org.id)) {
        seen.add(org.id);
        orgs.push(org);
      }
    };
    if (timberRfqProject) {
      for (const orgId of rfqProjectSupplierOrgIds(timberRfqProject)) {
        push(supplierOrgById?.get(orgId));
      }
    }
    if (!orgs.length && supplierOrgById) {
      supplierOrgById.forEach(org => {
        if (orgHasCapability(normalizeSupplierOrg(org), 'timber')) push(org);
      });
    }
    return orgs.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [timberRfqProject, supplierOrgById]);

  const filteredOrgs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const sorted = [...cncCapableOrgs].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    if (!q) return sorted;
    return sorted.filter((org) => org.name.toLowerCase().includes(q));
  }, [filter, cncCapableOrgs]);

  const toggleSupplier = (orgId: string, checked: boolean) => {
    setError(null);
    if (checked) {
      setSelectedOrgIds((prev) => (prev.includes(orgId) ? prev : [...prev, orgId]));
      const org = cncCapableOrgs.find(o => o.id === orgId);
      if (org) {
        setShipToByOrg(prev =>
          prev[orgId]
            ? prev
            : {
                ...prev,
                [orgId]: defaultShipToConfigForOrg(org, {
                  workType: 'cnc_quote',
                  timberProject: timberRfqProject,
                  cncProject: project,
                }),
              },
        );
      }
      return;
    }
    setSelectedOrgIds((prev) => prev.filter((id) => id !== orgId));
    setShipToByOrg(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
    setAssignmentsByOrg(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
    setStructureScopeByOrg(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
    setStructuresByOrg(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
  };

  // Structure scope changed — re-prefill this org's assignment board feet from the new scope.
  const reprefillOrgBoardFeet = (
    orgId: string,
    scope: 'all' | 'selected',
    structures: TimberSupplierStructureRef[],
  ) => {
    const bdft = proposalBoardFeetBySpecies(scopedNodeSets(scope, structures));
    setAssignmentsByOrg(prev => {
      const current = prev[orgId];
      if (!current?.length) return prev;
      return {
        ...prev,
        [orgId]: current.map(a => ({ ...a, boardFeet: bdft[a.species] || undefined })),
      };
    });
  };

  const setStructureScope = (orgId: string, scope: 'all' | 'selected') => {
    setError(null);
    setStructureScopeByOrg(prev => ({ ...prev, [orgId]: scope }));
    reprefillOrgBoardFeet(orgId, scope, structuresByOrg[orgId] ?? []);
  };

  const toggleStructure = (orgId: string, ns: ProposalNodeSet) => {
    setError(null);
    const current = structuresByOrg[orgId] ?? [];
    const has = current.some(ref => structureRefMatchesNodeSet(ref, ns));
    const next = has
      ? current.filter(ref => !structureRefMatchesNodeSet(ref, ns))
      : [...current, { id: ns.id, name: ns.name }];
    setStructuresByOrg(prev => ({ ...prev, [orgId]: next }));
    reprefillOrgBoardFeet(orgId, structureScopeByOrg[orgId] ?? 'all', next);
  };

  const toggleAssignedSpecies = (orgId: string, species: string) => {
    setError(null);
    const bdft = bdftBySpeciesForOrg(orgId);
    setAssignmentsByOrg(prev => {
      const current = prev[orgId] ?? [];
      const has = current.some(a => a.species === species);
      const next = has
        ? current.filter(a => a.species !== species)
        : [
            ...current,
            {
              species,
              ...(bdft[species] ? { boardFeet: bdft[species] } : {}),
              materialSource: 'supplier_provided' as const,
            },
          ];
      return { ...prev, [orgId]: next };
    });
  };

  const updateAssignment = (orgId: string, species: string, patch: Partial<CncSpeciesAssignment>) => {
    setError(null);
    setAssignmentsByOrg(prev => ({
      ...prev,
      [orgId]: (prev[orgId] ?? []).map(a => (a.species === species ? { ...a, ...patch } : a)),
    }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (!selectedOrgIds.length) {
        await onSave({
          supplierOrgIds: [],
          supplierOrgId: '',
          timberSupplierInvites: [],
          approvedSupplierOrgId: undefined,
        });
        onClose();
        return;
      }
      const timberSupplierInvites = buildTimberSupplierInvites(
        selectedOrgIds,
        {},
        project.timberSupplierInvites,
        undefined,
        undefined,
        Object.fromEntries(
          selectedOrgIds.flatMap(orgId => {
            const org = cncCapableOrgs.find(o => o.id === orgId);
            const config =
              shipToByOrg[orgId]
              ?? (org
                ? defaultShipToConfigForOrg(org, {
                    workType: 'cnc_quote',
                    timberProject: timberRfqProject,
                    cncProject: project,
                  })
                : undefined);
            return config ? [[orgId, config]] : [];
          }),
        ),
        Object.fromEntries(
          selectedOrgIds.map(orgId => [
            orgId,
            structureScopeByOrg[orgId] === 'selected'
              ? { scope: 'selected' as const, structures: structuresByOrg[orgId] ?? [] }
              : { scope: 'all' as const, structures: [] },
          ]),
        ),
        Object.fromEntries(selectedOrgIds.map(orgId => [orgId, assignmentsByOrg[orgId] ?? []])),
      );
      const primaryId = selectedOrgIds[0];
      const primaryName = cncCapableOrgs.find((o) => o.id === primaryId)?.name;
      const approvedId = project.approvedSupplierOrgId;
      await onSave({
        supplierOrgIds: selectedOrgIds,
        supplierOrgId: primaryId,
        supplierOrgName: primaryName,
        timberSupplierInvites,
        approvedSupplierOrgId:
          approvedId && selectedOrgIds.includes(approvedId) ? approvedId : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save suppliers.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="ops-modal-overlay srfq-timber-suppliers-modal-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="ops-modal srfq-timber-suppliers-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="srfq-cnc-suppliers-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ops-modal-header srfq-timber-suppliers-modal-header">
          <div className="srfq-timber-suppliers-modal-title-block">
            <Users size={16} aria-hidden className="srfq-timber-suppliers-modal-icon" />
            <h3 id="srfq-cnc-suppliers-modal-title">CNC suppliers</h3>
          </div>
          <button
            type="button"
            className="ops-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body srfq-timber-suppliers-modal-body">
          <p className="srfq-timber-suppliers-modal-lead">
            Choose CNC-capable suppliers for this quote request. Scope each shop to the structures and species it
            will cut, declare who provides the raw stock, and specify where finished packages should be delivered.
          </p>

          <input
            type="search"
            className="ops-modal-input"
            placeholder="Filter suppliers…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            disabled={submitting}
          />

          <div className="srfq-timber-suppliers-modal-list" role="list">
            {filteredOrgs.length === 0 ? (
              <p className="srfq-muted srfq-timber-suppliers-modal-empty">No matching suppliers.</p>
            ) : (
              filteredOrgs.map((org) => {
                if (!org.id) return null;
                const checked = selectedSet.has(org.id);
                const assigned = assignmentsByOrg[org.id] ?? [];
                const orgBdft = checked ? bdftBySpeciesForOrg(org.id) : {};
                const orgSpeciesOptions = checked ? speciesOptionsForOrg(org.id, orgBdft) : [];

                return (
                  <div
                    key={org.id}
                    className={`srfq-timber-suppliers-modal-row ${checked ? 'selected' : ''}`}
                    role="listitem"
                  >
                    <label className="srfq-timber-suppliers-modal-supplier">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={submitting}
                        onChange={(e) => toggleSupplier(org.id!, e.target.checked)}
                      />
                      <span className="srfq-timber-suppliers-modal-supplier-name">{org.name}</span>
                    </label>

                    {checked && proposalNodeSets.length > 0 && (
                      <div className="srfq-timber-suppliers-modal-structures">
                        <span className="srfq-timber-suppliers-modal-species-label">Structures to quote</span>
                        <div
                          className="srfq-timber-suppliers-modal-structure-toggle"
                          role="radiogroup"
                          aria-label={`Structure scope for ${org.name}`}
                        >
                          <button
                            type="button"
                            role="radio"
                            aria-checked={structureScopeByOrg[org.id] !== 'selected'}
                            className={`srfq-timber-suppliers-modal-scope-btn${
                              structureScopeByOrg[org.id] !== 'selected' ? ' on' : ''
                            }`}
                            disabled={submitting}
                            onClick={() => setStructureScope(org.id!, 'all')}
                          >
                            All structures
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={structureScopeByOrg[org.id] === 'selected'}
                            className={`srfq-timber-suppliers-modal-scope-btn${
                              structureScopeByOrg[org.id] === 'selected' ? ' on' : ''
                            }`}
                            disabled={submitting}
                            onClick={() => setStructureScope(org.id!, 'selected')}
                          >
                            Selected structures
                          </button>
                        </div>
                        {structureScopeByOrg[org.id] === 'selected' && (
                          <>
                            <ul className="srfq-timber-suppliers-modal-structure-options">
                              {proposalNodeSets.map(ns => {
                                const isOn = (structuresByOrg[org.id!] ?? []).some(ref =>
                                  structureRefMatchesNodeSet(ref, ns),
                                );
                                return (
                                  <li key={ns.id}>
                                    <label
                                      className={`srfq-timber-suppliers-modal-species-chip ${isOn ? 'on' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isOn}
                                        disabled={submitting}
                                        onChange={() => toggleStructure(org.id!, ns)}
                                      />
                                      <span>{ns.name}</span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                            {(structuresByOrg[org.id!] ?? []).length === 0 && (
                              <p className="srfq-timber-suppliers-modal-catalog-hint">
                                No structures selected — this supplier will receive all structures until at
                                least one is chosen.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {checked && (
                      <div className="srfq-timber-suppliers-modal-species">
                        <span className="srfq-timber-suppliers-modal-species-label">Species to cut</span>
                        {orgSpeciesOptions.length > 0 ? (
                          <ul className="srfq-timber-suppliers-modal-species-options">
                            {orgSpeciesOptions.map((species) => {
                              const isOn = assigned.some(a => a.species === species);
                              return (
                                <li key={species}>
                                  <label
                                    className={`srfq-timber-suppliers-modal-species-chip ${isOn ? 'on' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isOn}
                                      disabled={submitting}
                                      onChange={() => toggleAssignedSpecies(org.id!, species)}
                                    />
                                    <span>{species}</span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="srfq-timber-suppliers-modal-catalog-hint">
                            {proposalLoaded
                              ? 'No timber species found on the proposal — this quote request covers the full package.'
                              : 'Loading proposal species…'}
                          </p>
                        )}
                        {orgSpeciesOptions.length > 0 && assigned.length === 0 && (
                          <p className="srfq-timber-suppliers-modal-catalog-hint">
                            No species assigned — this supplier is asked to quote the full package.
                          </p>
                        )}

                        {assigned.map((a) => (
                          <div className="srfq-cnc-assignment" key={a.species}>
                            <div className="srfq-cnc-assignment-head">
                              <span className="srfq-cnc-assignment-species">{a.species}</span>
                              <label className="srfq-cnc-assignment-bdft">
                                <input
                                  type="number"
                                  min={0}
                                  className="ops-modal-input"
                                  value={a.boardFeet ?? ''}
                                  placeholder={orgBdft[a.species] ? String(orgBdft[a.species]) : '0'}
                                  disabled={submitting}
                                  aria-label={`Board feet of ${a.species} for ${org.name}`}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    updateAssignment(org.id!, a.species, {
                                      boardFeet: Number.isFinite(v) && v > 0 ? v : undefined,
                                    });
                                  }}
                                />
                                <span>bdft</span>
                              </label>
                            </div>
                            <div
                              className="srfq-timber-suppliers-modal-structure-toggle"
                              role="radiogroup"
                              aria-label={`Material source for ${a.species}`}
                            >
                              <button
                                type="button"
                                role="radio"
                                aria-checked={a.materialSource === 'supplier_provided'}
                                className={`srfq-timber-suppliers-modal-scope-btn${
                                  a.materialSource === 'supplier_provided' ? ' on' : ''
                                }`}
                                disabled={submitting}
                                onClick={() =>
                                  updateAssignment(org.id!, a.species, { materialSource: 'supplier_provided' })
                                }
                              >
                                Shop provides stock
                              </button>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={a.materialSource === 'lctf_shipped'}
                                className={`srfq-timber-suppliers-modal-scope-btn${
                                  a.materialSource === 'lctf_shipped' ? ' on' : ''
                                }`}
                                disabled={submitting}
                                onClick={() =>
                                  updateAssignment(org.id!, a.species, { materialSource: 'lctf_shipped' })
                                }
                              >
                                LCTF ships stock
                              </button>
                            </div>
                            {a.materialSource === 'lctf_shipped' && (
                              <label className="srfq-cnc-assignment-source">
                                <span>Timber sourced from</span>
                                <select
                                  className="ops-modal-input"
                                  value={a.sourceSupplierOrgId ?? ''}
                                  disabled={submitting}
                                  aria-label={`Source timber supplier for ${a.species}`}
                                  onChange={(e) => {
                                    const id = e.target.value;
                                    const name = sourceSupplierOptions.find(o => o.id === id)?.name;
                                    updateAssignment(org.id!, a.species, {
                                      sourceSupplierOrgId: id || undefined,
                                      sourceSupplierName: id ? name : undefined,
                                    });
                                  }}
                                >
                                  <option value="">Supplier TBD</option>
                                  {sourceSupplierOptions.map(o => (
                                    <option key={o.id} value={o.id}>
                                      {o.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}
                            <input
                              type="text"
                              className="ops-modal-input srfq-cnc-assignment-note"
                              placeholder="Note for this species (optional)"
                              value={a.note ?? ''}
                              disabled={submitting}
                              aria-label={`Note for ${a.species}`}
                              onChange={(e) =>
                                updateAssignment(org.id!, a.species, { note: e.target.value })
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {checked && (
                      <SupplierShipToFields
                        org={org}
                        projectSite={projectSite}
                        workType="cnc_quote"
                        timberProject={timberRfqProject}
                        cncProject={project}
                        config={
                          shipToByOrg[org.id]
                          ?? defaultShipToConfigForOrg(org, {
                            workType: 'cnc_quote',
                            timberProject: timberRfqProject,
                            cncProject: project,
                          })
                        }
                        disabled={submitting}
                        onChange={next =>
                          setShipToByOrg(prev => ({
                            ...prev,
                            [org.id!]: next,
                          }))
                        }
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {error && (
            <div className="srfq-timber-suppliers-modal-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </div>
          )}
        </div>

        <footer className="ops-modal-footer">
          <button
            type="button"
            className="ops-hide-completed-btn"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ops-new-btn"
            onClick={() => void handleSave()}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="srfq-timber-suppliers-modal-spin" aria-hidden /> Saving…
              </>
            ) : (
              'Save suppliers'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CncRfqSuppliersModal;
